import {
  boundsOfPoints,
  ellipseBorderPointTowards,
  rectBorderPointTowards,
  rectCenter,
  sampleCubic,
  vAdd,
  vDist,
  vLerp,
  vMul,
  vNorm,
  vRotate,
  vSub,
} from './geometry.js';
import { C, L, M, type Path } from './path.js';
import type {
  ArrowHead,
  BoardNode,
  ConnectorEndpoint,
  ConnectorNode,
  NodeId,
  Rect,
  Vec,
} from './types.js';

export type NodeLookup = (id: NodeId) => BoardNode | undefined;

function nodeBox(node: BoardNode): Rect {
  return { x: node.x, y: node.y, w: node.w, h: node.h };
}

/**
 * Where a connector attaches to a node. `auto` anchors slide along the border
 * so the line always points at the far endpoint, which is what makes diagrams
 * stay readable when boxes move.
 */
function attachPoint(node: BoardNode, target: Vec): Vec {
  const box = nodeBox(node);
  if (node.type === 'shape' && (node.shape === 'ellipse' || node.shape === 'halfCircle')) {
    return ellipseBorderPointTowards(box, target, node.rotation);
  }
  return rectBorderPointTowards(box, target, node.rotation);
}

function fixedAnchorPoint(node: BoardNode, nx: number, ny: number): Vec {
  const p = { x: node.x + node.w * nx, y: node.y + node.h * ny };
  return node.rotation === 0 ? p : vRotate(p, rectCenter(nodeBox(node)), node.rotation);
}

export function endpointCenter(ep: ConnectorEndpoint, lookup: NodeLookup): Vec {
  if (ep.kind === 'point') return { x: ep.x, y: ep.y };
  const node = lookup(ep.nodeId);
  if (!node) return { x: 0, y: 0 };
  if (ep.anchor.kind === 'fixed') return fixedAnchorPoint(node, ep.anchor.nx, ep.anchor.ny);
  return rectCenter(nodeBox(node));
}

export function resolveEndpoint(
  ep: ConnectorEndpoint,
  towards: Vec,
  lookup: NodeLookup,
  /** Extra gap between the node border and the line, in board units. */
  gap = 0,
): Vec {
  if (ep.kind === 'point') return { x: ep.x, y: ep.y };
  const node = lookup(ep.nodeId);
  if (!node) return { x: 0, y: 0 };
  const base =
    ep.anchor.kind === 'fixed'
      ? fixedAnchorPoint(node, ep.anchor.nx, ep.anchor.ny)
      : attachPoint(node, towards);
  if (gap <= 0) return base;
  const away = vNorm(vSub(towards, base));
  return vAdd(base, vMul(away, gap));
}

/** Outward normal at an endpoint, used to shape elbow and curved routes. */
function endpointNormal(ep: ConnectorEndpoint, at: Vec, lookup: NodeLookup, fallback: Vec): Vec {
  if (ep.kind === 'point') return fallback;
  const node = lookup(ep.nodeId);
  if (!node) return fallback;
  const c = rectCenter(nodeBox(node));
  const local = node.rotation === 0 ? at : vRotate(at, c, -node.rotation);
  const dx = (local.x - c.x) / (node.w / 2 || 1);
  const dy = (local.y - c.y) / (node.h / 2 || 1);
  const n: Vec =
    Math.abs(dx) >= Math.abs(dy)
      ? { x: Math.sign(dx) || 1, y: 0 }
      : { x: 0, y: Math.sign(dy) || 1 };
  return node.rotation === 0 ? n : vRotate(n, { x: 0, y: 0 }, node.rotation);
}

function elbowPoints(a: Vec, b: Vec, na: Vec, nb: Vec, bend: Vec | undefined): Vec[] {
  if (bend) {
    // A dragged bend pins the corner; route through it on both axes.
    return Math.abs(na.x) > Math.abs(na.y)
      ? [a, { x: bend.x, y: a.y }, { x: bend.x, y: b.y }, b]
      : [a, { x: a.x, y: bend.y }, { x: b.x, y: bend.y }, b];
  }

  const horizontalExit = Math.abs(na.x) > Math.abs(na.y);
  const horizontalEntry = Math.abs(nb.x) > Math.abs(nb.y);

  if (horizontalExit && horizontalEntry) {
    const mx = (a.x + b.x) / 2;
    return [a, { x: mx, y: a.y }, { x: mx, y: b.y }, b];
  }
  if (!horizontalExit && !horizontalEntry) {
    const my = (a.y + b.y) / 2;
    return [a, { x: a.x, y: my }, { x: b.x, y: my }, b];
  }
  if (horizontalExit) return [a, { x: b.x, y: a.y }, b];
  return [a, { x: a.x, y: b.y }, b];
}

export interface ConnectorGeometry {
  /** Polyline approximation, used for hit-testing, bounds and label placement. */
  points: Vec[];
  /** Drawing path; curved routes keep their bezier form here. */
  path: Path;
  start: Vec;
  end: Vec;
  /** Unit direction entering the start head (pointing away from the line). */
  startDir: Vec;
  /** Unit direction leaving the end head. */
  endDir: Vec;
  /** Point at 50% arc length, where the label sits. */
  mid: Vec;
}

export function connectorGeometry(node: ConnectorNode, lookup: NodeLookup): ConnectorGeometry {
  // Resolve against the opposite side's centre first, then refine once, which
  // is enough for auto anchors to settle on sensible sides of both nodes.
  const roughA = endpointCenter(node.start, lookup);
  const roughB = endpointCenter(node.end, lookup);
  const a = resolveEndpoint(node.start, roughB, lookup, 0);
  const b = resolveEndpoint(node.end, roughA, lookup, 0);
  const na = endpointNormal(node.start, a, lookup, vNorm(vSub(b, a)));
  const nb = endpointNormal(node.end, b, lookup, vNorm(vSub(a, b)));
  const bend = node.bend[0];

  let points: Vec[];
  let path: Path;

  if (node.routing === 'elbow') {
    points = elbowPoints(a, b, na, nb, bend);
    path = [M(a.x, a.y), ...points.slice(1).map((p) => L(p.x, p.y))];
  } else if (node.routing === 'curved') {
    const dist = Math.max(vDist(a, b), 1);
    const pull = Math.min(dist * 0.45, 220);
    const c1 = bend ? bend : vAdd(a, vMul(na, pull));
    const c2 = bend ? bend : vAdd(b, vMul(nb, pull));
    points = sampleCubic(a, c1, c2, b, 32);
    path = [M(a.x, a.y), C(c1.x, c1.y, c2.x, c2.y, b.x, b.y)];
  } else {
    points = bend ? [a, bend, b] : [a, b];
    path = [M(a.x, a.y), ...points.slice(1).map((p) => L(p.x, p.y))];
  }

  const startDir = vNorm(vSub(points[0]!, points[1] ?? points[0]!));
  const endDir = vNorm(vSub(points[points.length - 1]!, points[points.length - 2] ?? b));

  return {
    points,
    path,
    start: a,
    end: b,
    startDir: startDir.x === 0 && startDir.y === 0 ? { x: -1, y: 0 } : startDir,
    endDir: endDir.x === 0 && endDir.y === 0 ? { x: 1, y: 0 } : endDir,
    mid: pointAtFraction(points, 0.5),
  };
}

/** Point at a fraction of total arc length along a polyline. */
export function pointAtFraction(points: readonly Vec[], t: number): Vec {
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1) return points[0]!;
  let total = 0;
  const segs: number[] = [];
  for (let i = 1; i < points.length; i++) {
    const d = vDist(points[i - 1]!, points[i]!);
    segs.push(d);
    total += d;
  }
  if (total === 0) return points[0]!;
  let target = total * Math.min(Math.max(t, 0), 1);
  for (let i = 0; i < segs.length; i++) {
    const d = segs[i]!;
    if (target <= d) return vLerp(points[i]!, points[i + 1]!, d === 0 ? 0 : target / d);
    target -= d;
  }
  return points[points.length - 1]!;
}

export function connectorBounds(node: ConnectorNode, lookup: NodeLookup): Rect {
  const geo = connectorGeometry(node, lookup);
  const r = boundsOfPoints(geo.points);
  const pad = node.strokeWidth * 2 + 8;
  return { x: r.x - pad, y: r.y - pad, w: r.w + pad * 2, h: r.h + pad * 2 };
}

// ---------------------------------------------------------------------------
// Arrow heads
// ---------------------------------------------------------------------------

export interface ArrowHeadGeometry {
  path: Path;
  /** Whether the renderer should fill rather than stroke it. */
  filled: boolean;
  /**
   * How far to pull the line back from the tip so a filled head does not show
   * the line poking through its point.
   */
  inset: number;
}

/**
 * `at` is the tip, `dir` points away from the line (outwards).
 */
export function arrowHeadGeometry(
  head: ArrowHead,
  at: Vec,
  dir: Vec,
  strokeWidth: number,
): ArrowHeadGeometry | null {
  if (head === 'none') return null;
  const size = Math.max(strokeWidth * 3.2, 9);
  const d = vNorm(dir);
  const back = vMul(d, -1);
  const perp = { x: -d.y, y: d.x };

  const tail = vAdd(at, vMul(back, size));
  const w = size * 0.5;
  const left = vAdd(tail, vMul(perp, w));
  const right = vSub(tail, vMul(perp, w));

  switch (head) {
    case 'arrow':
      return {
        path: [M(left.x, left.y), L(at.x, at.y), L(right.x, right.y)],
        filled: false,
        inset: 0,
      };
    case 'triangle':
      return {
        path: [M(at.x, at.y), L(left.x, left.y), L(right.x, right.y), { c: 'Z' }],
        filled: false,
        inset: size * 0.9,
      };
    case 'filledTriangle':
      return {
        path: [M(at.x, at.y), L(left.x, left.y), L(right.x, right.y), { c: 'Z' }],
        filled: true,
        inset: size * 0.9,
      };
    case 'bar':
      return {
        path: [M(left.x, left.y), L(right.x, right.y)],
        filled: false,
        inset: 0,
      };
    case 'circle':
    case 'filledCircle': {
      const r = size * 0.36;
      const c = vAdd(at, vMul(back, r));
      return {
        path: circlePath(c, r),
        filled: head === 'filledCircle',
        inset: r * 2,
      };
    }
    case 'diamond': {
      const half = size * 0.45;
      const c = vAdd(at, vMul(back, half));
      const p1 = vAdd(c, vMul(d, half));
      const p2 = vAdd(c, vMul(perp, half * 0.72));
      const p3 = vAdd(c, vMul(back, half));
      const p4 = vSub(c, vMul(perp, half * 0.72));
      return {
        path: [
          M(p1.x, p1.y),
          L(p2.x, p2.y),
          L(p3.x, p3.y),
          L(p4.x, p4.y),
          { c: 'Z' },
        ],
        filled: true,
        inset: half * 2,
      };
    }
  }
}

const KAPPA = 0.5522847498307936;

function circlePath(c: Vec, r: number): Path {
  const o = r * KAPPA;
  return [
    M(c.x, c.y - r),
    C(c.x + o, c.y - r, c.x + r, c.y - o, c.x + r, c.y),
    C(c.x + r, c.y + o, c.x + o, c.y + r, c.x, c.y + r),
    C(c.x - o, c.y + r, c.x - r, c.y + o, c.x - r, c.y),
    C(c.x - r, c.y - o, c.x - o, c.y - r, c.x, c.y - r),
    { c: 'Z' },
  ];
}

export const ARROW_HEADS: ArrowHead[] = [
  'none',
  'arrow',
  'triangle',
  'filledTriangle',
  'circle',
  'filledCircle',
  'diamond',
  'bar',
];
